import React, { useState } from 'react';
import axios from 'axios';

const ExportDialog = ({ teamId, teamName, dateFrom, dateTo, onClose, isOpen }) => {
  const [exporting, setExporting] = useState(false);
  const [format, setFormat] = useState('csv');
  const [message, setMessage] = useState(null);

  if (!isOpen) return null;

  const handleExport = async () => {
    try {
      setExporting(true);
      setMessage(null);

      const response = await axios.post(
        '/api/reports/export',
        {},
        {
          params: {
            teamId,
            from: dateFrom,
            to: dateTo,
            format
          },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );

      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const ext = format === 'csv' ? 'csv' : 'html';
      link.setAttribute('download', `${teamName}_report_${dateFrom}_to_${dateTo}.${ext}`);
      
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      setMessage({ type: 'success', text: `Report exported successfully as ${format.toUpperCase()}` });
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      console.error('Export error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to export report'
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
        {/* Header */}
        <div className="border-b p-6">
          <h2 className="text-xl font-bold text-gray-900">📥 Export Report</h2>
          <p className="text-sm text-gray-600 mt-1">
            Export {teamName} report for {dateFrom} to {dateTo}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {message && (
            <div className={`p-4 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {message.text}
            </div>
          )}

          {/* Format Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Export Format
            </label>
            <div className="space-y-2">
              {[
                {
                  value: 'csv',
                  label: '📊 CSV Format',
                  description: 'Comma-separated values for Excel/Sheets'
                },
                {
                  value: 'html',
                  label: '📄 HTML Format',
                  description: 'HTML file that can be printed to PDF'
                }
              ].map(opt => (
                <label key={opt.value} className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="format"
                    value={opt.value}
                    checked={format === opt.value}
                    onChange={(e) => setFormat(e.target.value)}
                    className="w-4 h-4"
                  />
                  <div>
                    <p className="font-medium text-gray-900">{opt.label}</p>
                    <p className="text-xs text-gray-500">{opt.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <strong>Tip:</strong> {format === 'csv'
                ? 'Open the CSV file in Excel or Google Sheets for easy analysis and charts.'
                : 'Open the HTML file in your browser and use Print to save as PDF.'
              }
            </p>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="border-t p-6 flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={exporting}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {exporting ? (
              <>
                <span className="animate-spin">⏳</span>
                Exporting...
              </>
            ) : (
              <>
                📥 Export as {format.toUpperCase()}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportDialog;
